import { useEffect, useMemo, useState } from 'react';
import { talentApi } from '../../../api/talent.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Goals tree with alignment + check-ins. Create a goal, align children to a
 * parent, and record check-ins (progress cascades up to the parent).
 */
export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  function refresh() {
    setLoading(true);
    Promise.all([talentApi.listGoals(), hrApi.listEmployees().catch(() => ({ items: [] }))])
      .then(([g, e]) => {
        setGoals(g?.items || []);
        setEmployees(e?.items || e || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  const roots = useMemo(() => goals.filter((g) => !g.parent_goal_id), [goals]);
  const childrenOf = (id) => goals.filter((g) => g.parent_goal_id === id);

  async function createGoal(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await talentApi.createGoal({
        employeeId: fd.get('employeeId'),
        title: fd.get('title'),
        metric: fd.get('metric') || null,
        target: fd.get('target') || null,
        period: fd.get('period') || null,
        parentGoalId: fd.get('parentGoalId') || null,
        progress: Number(fd.get('progress')) || 0,
        status: fd.get('status') || 'active',
      });
      e.target.reset();
      setShowCreate(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create goal');
    }
  }

  async function addCheckin(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await talentApi.addCheckin(fd.get('goalId'), {
        note: fd.get('note') || null,
        progress: fd.get('progress') === '' ? null : Number(fd.get('progress')),
      });
      e.target.reset();
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add check-in');
    }
  }

  const GoalRow = ({ g, depth }) => (
    <>
      <tr data-testid={`talent-goal-row-${g.id}`} data-goal-id={g.id}>
        <td style={{ paddingLeft: 12 + depth * 24 }}>{depth > 0 ? '↳ ' : ''}{g.title}</td>
        <td>{g.employee_name || '—'}</td>
        <td data-testid={`talent-goal-progress-${g.id}`}>{Number(g.progress)}%</td>
        <td><span className="chip">{g.status}</span></td>
        <td>{g.period || '—'}</td>
      </tr>
      {childrenOf(g.id).map((c) => <GoalRow key={c.id} g={c} depth={depth + 1} />)}
    </>
  );

  return (
    <section data-testid="talent-goals-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Goals &amp; Alignment</h1>
          <p className="muted">{goals.length} goals · {roots.length} top-level</p>
        </div>
        <button className="btn primary" data-testid="talent-goal-create" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Close' : '+ Goal'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="talent-goals-error">{error}</div>}

      {showCreate && (
        <form onSubmit={createGoal} className="card form-card grid-form">
          <label>Employee
            <select name="employeeId" data-testid="talent-goal-employee-input" required defaultValue="">
              <option value="" disabled>— select —</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
            </select>
          </label>
          <label>Title<input name="title" data-testid="talent-goal-title-input" required /></label>
          <label>Metric<input name="metric" /></label>
          <label>Target<input name="target" /></label>
          <label>Period<input name="period" defaultValue="FY2026" /></label>
          <label>Progress<input name="progress" type="number" min="0" max="100" defaultValue="0" /></label>
          <label>Align to (parent)
            <select name="parentGoalId" defaultValue="">
              <option value="">— none —</option>
              {goals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
            </select>
          </label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="talent-goal-submit">Create goal</button></div>
        </form>
      )}

      <form onSubmit={addCheckin} className="card form-card grid-form">
        <h3 className="full">Add a check-in</h3>
        <label>Goal
          <select name="goalId" data-testid="talent-checkin-goal-input" required defaultValue="">
            <option value="" disabled>— select —</option>
            {goals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
          </select>
        </label>
        <label>Progress<input name="progress" type="number" min="0" max="100" data-testid="talent-checkin-progress-input" /></label>
        <label className="full">Note<input name="note" data-testid="talent-checkin-note-input" /></label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="talent-goal-checkin">Add check-in</button></div>
      </form>

      <div className="card">
        <table className="data-table" data-testid="talent-goal-list">
          <thead><tr><th>Goal</th><th>Owner</th><th>Progress</th><th>Status</th><th>Period</th></tr></thead>
          <tbody>
            {roots.map((g) => <GoalRow key={g.id} g={g} depth={0} />)}
            {!loading && goals.length === 0 && <tr><td colSpan={5} className="muted">No goals yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
